import Button from 'apollo-react/components/Button';
import qs from 'query-string';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { handleErrors } from '../../actions/ErrorActions';
import { getRecentSmartInsightItems } from '../../api/smart-insight';
import EditorDashboard from '../../components/EditorDashboard';
import Link from '../../components/Link';
import { RECENT_ENTITIES_QUANTITY } from '../../constants/EntityDesignerConstants';
import {
  REPORT_MODELS_SMARTINSIGHT_TYPE,
  REPORTS_SMARTINSIGHT_TYPE,
  SAVED_SEARCHES_SMARTINSIGHT_TYPE,
} from '../../constants/SmartInsightConstants';
import { STANDALONE_DASHBOARD_TYPES } from '../../constants/StandaloneLayoutConstants';
import { REPORT_DESIGNER, REPORT_MODEL_DESIGNER, SMART_INSIGHT } from '../../constants/Urls';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getToken } from '../../form-designer/selectors/common.selectors';
import { importReport } from '../../report-designer/actions/report-designer.actions';
import { importReportModel } from '../../report-model-designer/actions/report-model-designer.actions';
import { getSmartInsightApplicationURL } from '../../selectors/AuthSelectors';
import { getUITA } from '../../utils/commonUtils';
import {
  getLinkToLegacyReportViewer,
  getSmartInsightDashboardConfig,
  modifyRecentItems,
} from '../../utils/smartInsightUtils';
import NavigateLinkCell from '../FindPage/NavigateLinkCell';

const SmartInsightDashboard = ({ location }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const translateString = useTranslation();
  const smartInsightApplicationURL = useSelector(getSmartInsightApplicationURL);
  const token = useSelector(getToken);

  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [runSavedSearchConfig, setRunSavedSearchConfig] = useState({});
  const [isRunModalOpen, setIsRuntModalOpen] = useState(false);

  const { smartInsightType } = qs.parse(location.search) || {};

  const dashboardConfig = useMemo(() => {
    return getSmartInsightDashboardConfig(smartInsightType, translateString);
  }, [smartInsightType, translateString]);

  const {
    documentTitle,
    entityName,
    searchName,
    sortExpressionColumn,
    type,
    title,
    navigationColumnPathname,
  } = dashboardConfig;

  useEffect(() => {
    document.title = documentTitle;

    const loadRecent = async () => {
      setIsLoading(true);
      const response = await getRecentSmartInsightItems({
        entityName,
        searchName,
        sortExpressionColumn,
      });
      if (!response.hasError) {
        const { rows } = response.SearchData;
        const modifiedRows = modifyRecentItems({ items: rows, smartInsightType });
        setItems(modifiedRows);
      } else {
        dispatch(handleErrors(response.errors));
      }
      setIsLoading(false);
    };

    loadRecent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartInsightType]);

  const navigateToItemEditor = useCallback(
    ({ navigationParams, name, entityName, id, releaseType }) => {
      if (smartInsightType === REPORT_MODELS_SMARTINSIGHT_TYPE) {
        history.push(
          `${REPORT_MODEL_DESIGNER}?reportKey=${name}&reportId=${id}&standaloneLayout=true&releaseType=${releaseType}`
        );
      }

      if (smartInsightType === SAVED_SEARCHES_SMARTINSIGHT_TYPE) {
        history.push(`${navigationParams}&entityName=${entityName}&standaloneLayout=true`);
      }

      if (smartInsightType === REPORTS_SMARTINSIGHT_TYPE || !smartInsightType) {
        history.push(`${REPORT_DESIGNER}?reportId=${id}&standaloneLayout=true`);
      }
    },
    [history, smartInsightType]
  );

  const redirectToReportModel = ({ reportName, reportId }) => {
    history.push(
      `${REPORT_MODEL_DESIGNER}?reportKey=${reportName}&reportId=${reportId}&isImported=true&standaloneLayout=true`
    );
  };

  const redirectToReportDesigner = () => {
    history.push(`${REPORT_DESIGNER}?standaloneLayout=true&isImported=true`);
  };

  const importFile = useCallback(
    async (file) => {
      switch (type) {
        case STANDALONE_DASHBOARD_TYPES.REPORT_MODELS:
          await dispatch(importReportModel({ file, redirectToReportModel }));
          break;
        case STANDALONE_DASHBOARD_TYPES.REPORTS:
          await dispatch(importReport({ file, redirectToReportDesigner }));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type]
  );

  const savedSearchExtraColumns = [
    {
      index: 1,
      accessor: 'EntityName',
      header: translateString('Entity Name'),
      fixedWidth: true,
      customCell: ({ row }) => {
        return row.TranslatedEntityName || row.EntityName;
      },
    },
    {
      index: 6,
      accessor: '',
      header: '',
      fixedWidth: true,
      customCell: ({ row }) => {
        return (
          <Button
            variant="text"
            size="small"
            onClick={() => {
              setRunSavedSearchConfig({
                entityName: row?.EntityName,
                savedSearchName: row?.QYD_QUERY_NAME,
              });
              setIsRuntModalOpen(true);
            }}
          >
            {translateString('Run')}
          </Button>
        );
      },
    },
  ];

  const reportExtraColumns = [
    {
      index: 100,
      accessor: '',
      header: '',
      fixedWidth: true,
      customCell: ({ row }) => {
        return (
          <Button
            variant="text"
            size="small"
            component={Link}
            to={getLinkToLegacyReportViewer(smartInsightApplicationURL, row.QYD_ID, token)}
            target="_blank"
            ui-ta={getUITA('run-smartinsight-report')}
          >
            {translateString('Run')}
          </Button>
        );
      },
    },
  ];

  const extraColumns =
    type === STANDALONE_DASHBOARD_TYPES.SAVED_SEARCHES
      ? savedSearchExtraColumns
      : type === STANDALONE_DASHBOARD_TYPES.REPORTS
      ? reportExtraColumns
      : undefined;

  return (
    <EditorDashboard
      dashboardTitle={title}
      dashboardLink={SMART_INSIGHT}
      itemName={title}
      itemQuantity={RECENT_ENTITIES_QUANTITY}
      navigateToItemEditor={navigateToItemEditor}
      customCell={NavigateLinkCell.forUrl(navigationColumnPathname, '_blank')}
      isLoading={isLoading}
      items={items}
      itemTitle={`${translateString('Recent')} ${title}`}
      entityName={entityName}
      initialSearchName={searchName}
      extraColumns={extraColumns}
      dashboardType={type}
      runSavedSearchConfig={runSavedSearchConfig}
      isRunModalOpen={isRunModalOpen}
      setIsRuntModalOpen={setIsRuntModalOpen}
      importFile={importFile}
    />
  );
};

export default SmartInsightDashboard;
